import { urlParamsToFilters } from '@app/app-updates-table-filters/src/utils/urlFilterUtils';
import type {
  UpdatesSetType,
  UpdatesStateType,
  UpdatesCursorSetType,
  UpdatesLimitSetType,
  UpdatesLoadingSetType,
  UpdatesErrorSetType,
  FilterAppSelectType,
  FilterStatusSelectType,
  FilterChangeTypeSelectType,
  FilterSearchSelectType,
  UpdatesFiltersApplyType,
  UpdatesFiltersResetType,
  UpdatesDraftFiltersResetType,
  UpdatesFiltersStateType,
} from '../types';

type UpdateAction =
  | UpdatesSetType
  | UpdatesCursorSetType
  | UpdatesLimitSetType
  | UpdatesLoadingSetType
  | UpdatesErrorSetType
  | FilterAppSelectType
  | FilterStatusSelectType
  | FilterChangeTypeSelectType
  | FilterSearchSelectType
  | UpdatesFiltersApplyType
  | UpdatesFiltersResetType
  | UpdatesDraftFiltersResetType;

const urlFilters = urlParamsToFilters();

const initialFiltersState: UpdatesFiltersStateType = {
  selectedProducts: urlFilters.selectedProducts ?? null,
  selectedStatuses: urlFilters.selectedStatuses ?? null,
  selectedChangeTypes: urlFilters.selectedChangeTypes ?? null,
  searchQuery: urlFilters.searchQuery ?? null,
};

export const INITIAL_UPDATES_STATE: UpdatesStateType = {
  data: [],
  isLoading: false,
  error: null,
  links: {
    self: '',
    next: null,
    prev: null,
  },
  pagination: {
    cursor: urlFilters.cursor,
    limit: urlFilters.limit ?? 20,
  },
  draftFilters: { ...initialFiltersState },
  committedFilters: {
    ...initialFiltersState,
    committedFiltersVersion: 0,
  },
};

export const updatesReducer = (
  state: UpdatesStateType,
  action: UpdateAction,
): UpdatesStateType => {
  switch (action.type) {
    case 'UPDATES_DATA_LINKS_SET':
      return {
        ...state,
        data: action.payload.updates,
        links: action.payload.links,
      };

    case 'UPDATES_CURSOR_SET':
      return {
        ...state,
        pagination: {
          ...state.pagination,
          cursor: action.payload.cursor,
        },
      };

    case 'UPDATES_LIMIT_SET':
      return {
        ...state,
        pagination: {
          ...state.pagination,
          limit: action.payload.limit,
          cursor: undefined,
        },
      };

    case 'UPDATES_LOADING_SET':
      return {
        ...state,
        isLoading: action.payload.isLoading,
      };

    case 'UPDATES_ERROR_SET':
      return {
        ...state,
        error: action.payload.error,
      };

    case 'FILTER_APP_SELECT':
      return {
        ...state,
        draftFilters: {
          ...state.draftFilters,
          selectedProducts: action.payload.selectedProducts,
        },
      };

    case 'FILTER_STATUS_SELECT':
      return {
        ...state,
        draftFilters: {
          ...state.draftFilters,
          selectedStatuses: action.payload.selectedStatuses,
        },
      };

    case 'FILTER_CHANGE_TYPE_SELECT':
      return {
        ...state,
        draftFilters: {
          ...state.draftFilters,
          selectedChangeTypes: action.payload.selectedChangeTypes,
        },
      };

    case 'FILTER_SEARCH_SELECT':
      return {
        ...state,
        draftFilters: {
          ...state.draftFilters,
          searchQuery: action.payload.searchQuery,
        },
      };

    case 'UPDATES_FILTERS_APPLY':
      return {
        ...state,
        committedFilters: {
          ...state.draftFilters,
          committedFiltersVersion:
            state.committedFilters.committedFiltersVersion + 1,
        },
        pagination: {
          ...state.pagination,
          cursor: undefined,
        },
      };

    case 'UPDATES_FILTERS_RESET':
      return {
        ...state,
        draftFilters: {
          selectedProducts: null,
          selectedStatuses: null,
          selectedChangeTypes: null,
          searchQuery: null,
        },
        committedFilters: {
          selectedProducts: null,
          selectedStatuses: null,
          selectedChangeTypes: null,
          searchQuery: null,
          committedFiltersVersion:
            state.committedFilters.committedFiltersVersion + 1,
        },
        pagination: {
          ...state.pagination,
          cursor: undefined,
        },
      };

    case 'UPDATES_DRAFT_FILTERS_RESET':
      return {
        ...state,
        draftFilters: {
          selectedProducts: null,
          selectedStatuses: null,
          selectedChangeTypes: null,
          searchQuery: null,
        },
      };

    default:
      return state;
  }
};
